import type { LucideIcon } from 'lucide-react';

import { ActionIcon, Badge, Box, Collapse, Group, Paper, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { FieldHelpTooltip } from '../../../components/FieldHelpTooltip';

interface SpecSectionPanelProps {
  /** Header-right slot — e.g. the "+ Add Component"/"+ Add Range" buttons. */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Shown next to the title only while `collapsible` and the panel itself is closed — e.g. Price
   * Components' own selected-count badge, so the summary stays visible once its content is
   * hidden. Ignored while the panel is open. */
  collapsedBadge?: React.ReactNode;
  /** Adds a chevron toggle (defaults open) so the user can collapse this panel's content — for
   * panels tall/wide enough that several stacked at once gets crowded (e.g. Price Components and
   * Price Adjustment Ranges both full-width in the same Range card). */
  collapsible?: boolean;
  description?: string;
  /** Stretches the panel to fill its flex container's height (e.g. to match a sibling column) —
   * grows past that height instead of clipping if its content needs more room. */
  fillHeight?: boolean;
  icon: LucideIcon;
  /** Controlled open state — only needed by a caller that must force this open itself (e.g.
   * PriceComponentsTable reopening when a component is added). Falls back to internal state
   * (defaulting open) when omitted. */
  onToggle?: () => void;
  opened?: boolean;
  title: string;
  /** Extra detail shown via a help icon next to the title, for copy too long for `description`. */
  tooltip?: string;
}

/**
 * The icon + title (+ optional description/tooltip/collapsed-badge) block — clickable to toggle
 * collapse when `collapsible`, so the user isn't limited to the small chevron hit target.
 * Extracted purely to keep that conditional click-handling out of SpecSectionPanel's own
 * complexity.
 */
const SpecSectionPanelHeading: React.FC<{
  collapsedBadge?: React.ReactNode;
  collapsible?: boolean;
  description?: string;
  icon: LucideIcon;
  onToggle: () => void;
  opened: boolean;
  title: string;
  tooltip?: string;
}> = ({ collapsedBadge, collapsible, description, icon: Icon, onToggle, opened, title, tooltip }) => (
  <UnstyledButton
    onClick={collapsible ? onToggle : undefined}
    style={{ cursor: collapsible ? 'pointer' : 'default', flex: 1 }}
  >
    <Group align="flex-start" gap="sm" wrap="nowrap">
      <ThemeIcon color="blue" radius="md" size={32} variant="light">
        <Icon size={16} />
      </ThemeIcon>
      <Stack gap={0}>
        <Group gap={4} wrap="nowrap">
          <Text fw={700} fz="sm">
            {title}
          </Text>
          {tooltip ? <FieldHelpTooltip label={tooltip} /> : null}
          {collapsible && !opened && collapsedBadge ? (
            <Badge color="grape" size="xs" variant="light">
              {collapsedBadge}
            </Badge>
          ) : null}
        </Group>
        {description ? (
          <Text c="dimmed" fz="xs">
            {description}
          </Text>
        ) : null}
      </Stack>
    </Group>
  </UnstyledButton>
);

/**
 * Shared bordered-box header (icon + title + optional description/tooltip, optional header-right
 * action) used by every QualitySpecCard section — Configuration, Contracted Value, Applies to,
 * Adjustment Behavior, Price Components, Price Adjustment Ranges — so they read as one consistent
 * family of panels instead of each section inventing its own header markup.
 */
export const SpecSectionPanel: React.FC<SpecSectionPanelProps> = ({
  action,
  children,
  collapsedBadge,
  collapsible,
  description,
  fillHeight,
  icon,
  onToggle,
  opened: openedProp,
  title,
  tooltip,
}) => {
  const [openedState, { toggle: toggleState }] = useDisclosure(true);
  const opened = openedProp ?? openedState;
  const toggle = onToggle ?? toggleState;
  const content = fillHeight ? <Box style={{ flex: 1 }}>{children}</Box> : children;

  return (
    <Paper
      bg="var(--mantine-color-default-hover)"
      p="xs"
      radius="md"
      style={fillHeight ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}
      withBorder
    >
      <Stack gap="sm" style={fillHeight ? { flex: 1 } : undefined}>
        <Group align="flex-start" justify="space-between" wrap="nowrap">
          <SpecSectionPanelHeading
            collapsedBadge={collapsedBadge}
            collapsible={collapsible}
            description={description}
            icon={icon}
            onToggle={toggle}
            opened={opened}
            title={title}
            tooltip={tooltip}
          />
          <Group gap={4} wrap="nowrap">
            {action}
            {collapsible ? (
              <ActionIcon
                aria-label={opened ? `Collapse ${title}` : `Expand ${title}`}
                color="gray"
                onClick={toggle}
                size="sm"
                variant="subtle"
              >
                {opened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </ActionIcon>
            ) : null}
          </Group>
        </Group>
        {/* `flex: 1` (no min-height:0/overflow) only grows to fill leftover space — it never
            shrinks below its content, so a taller table still pushes this panel (and its
            ancestors) taller instead of clipping or scrolling. */}
        {collapsible ? <Collapse in={opened}>{content}</Collapse> : content}
      </Stack>
    </Paper>
  );
};
